import { createPath } from 'history';
import React, { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import ClipSelectPage, {
  ClipSuggestion,
  OnOpenInAdvancedEditor,
  OnReprocessClip,
  OnSubmit,
} from 'blocks/ClipSelect';

import {
  enableClipSuggestion,
  getGroupClipSuggestion,
  getGroupClipSuggestions,
} from 'redux/middleware/api/podcast-service/actions';
import {
  createGroupClipSuggestion,
  dislikeGroupSuggestedClip,
  reprocessGroupClipSuggestion,
} from 'redux/modules/clip-select';
import { groupRequestIdSelector } from 'redux/modules/entities';
import {
  onClickDownloadAllClips,
  onClickExportAllClips,
} from 'redux/modules/mixpanel';
import { showError } from 'redux/modules/notification';
import { Dispatch, State } from 'redux/types';
import { ClipSuggestionDislikeReason } from 'types';
import { getDownloadPath, getProjectPath } from 'utils/routes';
import { suggestedClipsSelector, suggestionSelector } from './selectors';
import useAutomationSuggestedClipInfo from './useAutomationSuggestedClipInfo';

export interface AutomationSuggestedClipsPageProps {}

type HandleSelectClip = React.ComponentProps<
  typeof ClipSelectPage
>['onSelectClip'];
type HandleDislikeClip = React.ComponentProps<
  typeof ClipSelectPage
>['onDislikeClip'];

const AutomationSuggestedClipsPage: React.FC<AutomationSuggestedClipsPageProps> = () => {
  const store = useStore<State>();
  const groupRequestId = useSelector(groupRequestIdSelector);

  const dispatch = useDispatch<Dispatch>();

  const {
    eddyProjectId,
    episodeId,
    subscriptionItemId,
    suggestedClips,
    dislikedSuggestionIds,
    clipsPageInfo,
    source,
  } = useAutomationSuggestedClipInfo();

  const handleSubmit: OnSubmit = async clipSuggestion => {
    try {
      await dispatch(enableClipSuggestion(clipSuggestion.id));

      return getDownloadPath(clipSuggestion.widgetId);
    } catch {
      dispatch(showError('Error sharing clip', 5));
      return undefined;
    }
  };

  const handleDislikeClip: HandleDislikeClip = useCallback(
    async (
      reason: ClipSuggestionDislikeReason,
      clipSuggestion: ClipSuggestion,
      onClipDisliked?: () => void,
    ): Promise<void> => {
      try {
        await dispatch(dislikeGroupSuggestedClip(reason, clipSuggestion.id));
        onClipDisliked?.();
      } catch {
        dispatch(showError({ message: 'Failed to remove clip.' }));
      }
    },
    [dispatch],
  );

  const handleSelectClip: HandleSelectClip = useCallback(
    async ({ region, activeSuggestionId }): Promise<number | void> => {
      const suggestionId = await dispatch(
        createGroupClipSuggestion(
          Number(episodeId),
          subscriptionItemId,
          groupRequestId,
          activeSuggestionId,
          region.startMillis,
          region.endMillis,
        ),
      );

      return suggestionId;
    },
    [dispatch, episodeId, groupRequestId, subscriptionItemId],
  );

  const handleReprocessClip: OnReprocessClip = useCallback(
    async (suggestionId, opts) => {
      const { configJson, context } = opts || {};

      const clip = suggestedClips.find(c => c.id === suggestionId);

      if (!clip) {
        return null;
      }

      await dispatch(
        reprocessGroupClipSuggestion(
          suggestionId,
          subscriptionItemId,
          groupRequestId,
          clip.startMillis,
          clip.endMillis,
          configJson,
          context === 'preview',
        ),
      );

      return null;
    },
    [dispatch, groupRequestId, subscriptionItemId, suggestedClips],
  );

  const handleOpenInAdvancedEditor: OnOpenInAdvancedEditor = useCallback(
    async ({ id, projectUuid }): Promise<string | undefined> => {
      try {
        await dispatch(enableClipSuggestion(id));
        return createPath(getProjectPath(projectUuid));
      } catch {
        dispatch(showError('Error while opening the clip', 5));
        return undefined;
      }
    },
    [dispatch],
  );

  const handleUpdateSuggestions = useCallback(async () => {
    await dispatch(
      getGroupClipSuggestions(
        Number(episodeId),
        subscriptionItemId,
        groupRequestId,
      ),
    );
    return suggestedClipsSelector(store.getState());
  }, [dispatch, episodeId, groupRequestId, store, subscriptionItemId]);

  const handleUpdateSuggestion = useCallback(
    async (id: number) => {
      await dispatch(getGroupClipSuggestion(id));
      return suggestionSelector(store.getState(), id);
    },
    [dispatch, store],
  );

  const handleDownloadAll = useCallback(() => {
    dispatch(onClickDownloadAllClips({ groupRequestId, subscriptionItemId }));
  }, [dispatch, groupRequestId, subscriptionItemId]);

  const handleExportAll = useCallback(() => {
    dispatch(onClickExportAllClips({ groupRequestId, subscriptionItemId }));
  }, [dispatch, groupRequestId, subscriptionItemId]);

  return (
    <ClipSelectPage
      {...{
        suggestedClips,
        eddyProjectId,
        dislikedSuggestionIds,
        clipsPageInfo,
        source,
      }}
      requestId={groupRequestId}
      onDislikeClip={handleDislikeClip}
      onReprocessClip={handleReprocessClip}
      onOpenInAdvancedEditor={handleOpenInAdvancedEditor}
      onSelectClip={handleSelectClip}
      onSubmit={handleSubmit}
      onUpdateSuggestions={handleUpdateSuggestions}
      onUpdateSuggestion={handleUpdateSuggestion}
      onDownloadAll={handleDownloadAll}
      onExportAll={handleExportAll}
    />
  );
};

export default AutomationSuggestedClipsPage;
